import React from 'react';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { validProps } from '../PageBuilder/Field';

import { IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { LayoutComposer, StaticPage, SectionBuilder } from '../PageBuilder/PageBuilder';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
import NewestListingsSection from './NewestListingsSection/NewestListingsSection';

import css from './LandingPage.module.css';

// New (24 sep 2026; repositioned twice since - see git history): the "newest listings" row (see
// NewestListingsSection.js) has to render at a fixed point among the homepage's sections, but
// Sharetribe Console's page builder has no concept of live listing data, so that row can't be one
// of the Console-managed sections in pageAssetsData. Instead, this page splits the Console-managed
// sections around the insertion point and renders the newest-listings row in between - instead of
// handing the whole section list to a single <PageBuilder> as before. Everything else
// (Topbar/Main/Footer layout, page meta/schema for SEO, the loading spinner, the maintenance-mode
// fallback) is copied from PageBuilder.js as-is. PageBuilder.js itself is untouched, so every other
// Console-built page (e.g. /p/vakantiewoning-verhuren) is unaffected.
//
// Current position (Ron, 24 sep 2026): between "Thema's voor bijzonder overnachten" and "Bijzonder
// duurzaam". "Bijzonder duurzaam" is a known, previously-confirmed rendered section id -
// `section-3` (see LandingPage.module.css's own #section-3 rules). The split point is found by
// looking up THAT id's position, rather than hardcoding a numeric index for "Thema's" itself
// (Console section order/count elsewhere isn't something this codebase can verify, so anchoring on
// the one id we do have confirmed is the more robust choice).
//
// Important: a Console section's *raw* `sectionId` field is usually empty - SectionBuilder only
// derives the actual "section-N" id at render time (see getRenderedSectionIds below), from the
// section's position among sections without their own explicit id. An earlier version of this fix
// compared section.sectionId directly and never found a match (row always fell back to rendering
// at the very end) - this version replicates SectionBuilder's own id-generation to find the right
// spot. If section-3 is ever renamed/removed in Console, INSERT_BEFORE_SECTION_ID below won't be
// found, and the row falls back to rendering after every section rather than disappearing or
// erroring.
//
// Trade-off worth knowing about: this page used to lazy-load PageBuilder's section-rendering code
// as its own separate chunk. Now that this page renders SectionBuilder directly, that code is part
// of this page's own chunk instead - the homepage was already its own lazy-loaded route chunk, so
// this doesn't affect any other page's bundle size, only makes the homepage's own chunk somewhat
// bigger.
const INSERT_BEFORE_SECTION_ID = 'section-3'; // "Bijzonder duurzaam"

// Copied from SectionBuilder.js's own (unexported) getUniqueSectionId, unchanged. Needed because
// a Console section's *raw* `sectionId` field is often empty/undefined - SectionBuilder only
// derives the actual "section-N" id operators see in the DOM (and that LandingPage.module.css's
// own #section-3 rules target) at render time, from the section's position among sections that
// don't have their own explicit id. Reusing this same algorithm here (instead of just comparing
// section.sectionId directly, which missed the real "Bijzonder duurzaam" section entirely - see
// the 24 sep 2026 fix that added this) is what makes INSERT_BEFORE_SECTION_ID actually match.
const getUniqueSectionId = (sectionId, index, seenIds) => {
  const candidate = sectionId || `section-${index + 1}`;
  if (seenIds.includes(candidate)) {
    let sequentialCandidate = `${candidate}1`;
    for (let i = 2; seenIds.includes(sequentialCandidate); i++) {
      sequentialCandidate = `${candidate}${i}`;
    }
    return getUniqueSectionId(sequentialCandidate, index, seenIds);
  } else {
    seenIds.push(candidate);
    return candidate;
  }
};

// Computes the same ids SectionBuilder would assign to each section, in order.
const getRenderedSectionIds = sections => {
  const seenIds = [];
  return sections.map((section, index) => getUniqueSectionId(section.sectionId, index, seenIds));
};

// Copied from PageBuilder.js's own (unexported) getMetadata, unchanged - kept in sync manually if
// that ever changes, since duplicating this ~20-line function locally was safer than modifying the
// shared PageBuilder.js file just to export it.
const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  const title = validProps(pageTitle, fieldOptions)?.content;
  const description = validProps(pageDescription, fieldOptions)?.content;
  const openGraph = validProps(socialSharing, fieldOptions);
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  const pageSchemaForSEO = {
    '@context': 'http://schema.org',
    '@type': schemaType || 'WebPage',
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, onManageDisableScrolling } = props;
  const data = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  // Console "Listings" sections with featured listings (Sharetribe 12.x) - same data PageBuilder
  // passes to its own SectionBuilder.
  const featuredListings = getFeaturedListingsProps(camelize(ASSET_NAME), props);

  if (!data && !inProgress && error) {
    return <FallbackPage error={error} />;
  }

  const { sections = [], meta = {} } = data || {};
  const pageMetaProps = getMetadata(meta, undefined, undefined);
  // Resolve each section's real rendered id up front, and bake it into an explicit sectionId on
  // every section object, BEFORE splitting the array. Splitting `sections` into two separate
  // <SectionBuilder> calls below means each call otherwise restarts its own local
  // index-based numbering (getUniqueSectionId falls back to `section-${localIndex + 1}` for any
  // section without an explicit id) - silently reassigning different ids than a single
  // <SectionBuilder> over the whole list would have. Pre-resolving and explicitly setting
  // sectionId here keeps every section's id identical to what it would be without this split
  // (also what LandingPage.module.css's #section-3/#section-6 rules were written against).
  const renderedSectionIds = getRenderedSectionIds(sections);
  const sectionsWithStableIds = sections.map((section, index) => ({
    ...section,
    sectionId: renderedSectionIds[index],
  }));
  const insertIndex = renderedSectionIds.indexOf(INSERT_BEFORE_SECTION_ID);
  const sectionsBeforeRow =
    insertIndex === -1 ? sectionsWithStableIds : sectionsWithStableIds.slice(0, insertIndex);
  const sectionsAfterRow = insertIndex === -1 ? [] : sectionsWithStableIds.slice(insertIndex);

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage {...pageMetaProps} className={css.landingPage}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer />
              </Topbar>
              <Main as="main" id="main-content" className={css.main}>
                {sections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <SectionBuilder
                      sections={sectionsBeforeRow}
                      options={{ featuredListings }}
                      onManageDisableScrolling={onManageDisableScrolling}
                    />
                    {/* <NewestListingsSection /> */}
                    <SectionBuilder
                      sections={sectionsAfterRow}
                      options={{ featuredListings }}
                      onManageDisableScrolling={onManageDisableScrolling}
                    />
                  </>
                )}
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  onManageDisableScrolling: null,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};
  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);
  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};
const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouterp HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
